import type { RescueDocumentType, RescueExemptionNotice, RescueRequiredDocument } from '../../lib/documentQueries';
import {
  certificateName,
  stateDocumentHint,
  stateDocumentKind,
  stateName,
  uploadedJustNow,
  withFileLimit,
} from '../../lib/documentLabels';
import { ClockIcon, CrossIcon } from '../Documents/DocumentIcons';
import { FileCard } from '../Documents/FileCard';
import StatusPill from '../Documents/StatusPill';
import UploadBox from '../Documents/UploadBox';
import { formatDate } from '../../lib/utils';
import styles from './OrderDocumentsStep.module.scss';

/** Named so the sentence that follows reads as being about this document. */
function documentName(entry: RescueRequiredDocument): string {
  if (entry.documentType === 'NONPROFIT_LETTER') {
    return '501(c)⁠(3) determination letter';
  }
  const filed = filedType(entry);
  if (filed !== 'STATE_EXEMPTION') {
    return stateDocumentKind(filed).toLowerCase();
  }
  return certificateName(entry.state);
}

/** What was just replaced, in a word where it can be. */
function replacedNoun(entry: RescueRequiredDocument): string {
  const filed = filedType(entry);
  if (filed === 'NONPROFIT_LETTER') {
    return 'letter';
  }
  if (filed === 'STATE_EXEMPTION') {
    return 'certificate';
  }
  return stateDocumentKind(filed).toLowerCase();
}

/** In a conditional state the filed document may be the application or the denial, not the certificate. */
function filedType(entry: RescueRequiredDocument): RescueDocumentType {
  if (entry.acceptedDocumentTypes.length > 1 && entry.document) {
    return entry.document.documentType;
  }
  return entry.documentType;
}

/** The panel's own heading: the mark, the title, and the sentence under it, all centred. */
function StateBlock({ tone, title, children }: { tone: 'wait' | 'bad'; title: string; children: React.ReactNode }) {
  return (
    <div className={styles.stateBlock}>
      <div
        className={`${styles.stateIc} ${tone === 'wait' ? styles.stateIcWait : styles.stateIcBad}`}
        aria-hidden="true"
      >
        {tone === 'wait' ? <ClockIcon /> : <CrossIcon />}
      </div>
      <h2 className={styles.stateT}>{title}</h2>
      <p className={styles.stateD}>{children}</p>
    </div>
  );
}

function UnfiledUpload({
  entry,
  uploading,
  onPickFile,
}: {
  entry: RescueRequiredDocument;
  uploading: boolean;
  onPickFile: (documentType: RescueRequiredDocument['documentType'], state: string | null) => void;
}) {
  return (
    <UploadBox
      compact
      title={`Upload your ${entry.documentType === 'NONPROFIT_LETTER' ? 'determination letter' : 'certificate'}`}
      hint="PDF up to 20MB."
      onUpload={() => onPickFile(entry.documentType, entry.state)}
      uploading={uploading}
    />
  );
}

/** The modal's own title, for the branch that still renders one above this panel. */
export function blockedNoticeTitle(required: RescueRequiredDocument[], notices: RescueExemptionNotice[]): string {
  if (notices.length > 0) {
    return "We can't work out your tax position";
  }
  if (required.some((entry) => entry.document?.status === 'REJECTED')) {
    return 'Document Not Accepted';
  }
  if (!required.some((entry) => entry.document?.status === 'PENDING')) {
    return 'Documents Needed';
  }
  return 'Documents Awaiting Review';
}

/**
 * Why this order can't go through, and whether the rescue can do anything about it.
 *
 * Four refusals reach here and they want different answers. A letter never filed is asked for. A rejected document is the rescue's
 * to fix, so it is replaced from inside this modal rather than sending them to the account page. A
 * document merely in review means the one order it covers is spent, and the only honest answer is
 * to wait. A notice means the jurisdiction itself has no position on file, which no upload clears.
 */
export default function OrderBlockedNotice({
  required,
  notices,
  shipToState,
  uploadingKey,
  keyFor,
  onPickFile,
  fileError,
  programName,
  justReplaced,
}: {
  required: RescueRequiredDocument[];
  notices: RescueExemptionNotice[];
  shipToState: string | null;
  uploadingKey: string | null;
  keyFor: (documentType: RescueRequiredDocument['documentType'], state: string | null) => string;
  onPickFile: (documentType: RescueRequiredDocument['documentType'], state: string | null) => void;
  fileError: string | null;
  /** The order they were trying to place, e.g. "Fi Mini". */
  programName: string;
  /** They replaced a document from inside this modal, so the wait is news rather than a refusal. */
  justReplaced: boolean;
}) {
  // Only the letter blocks before the form opens. A state certificate is the documents step's to
  // explain, where the rescue can still pick a different ship-to state.
  const letters = required.filter((entry) => entry.documentType === 'NONPROFIT_LETTER');
  const rejected = letters.filter((entry) => entry.document?.status === 'REJECTED');
  const inReview = letters.filter((entry) => entry.document?.status === 'PENDING');
  // Nothing filed against it at all, so it is collectable now even though the order still waits.
  // Only Fi Nano ever asks for a second document, so this is empty everywhere else.
  const unfiled = required.filter((entry) => entry.document === null && !entry.satisfied);

  if (notices.length > 0) {
    return (
      <StateBlock tone="bad" title="We can't work out your tax position">
        We can't yet work out the tax position for shipments to {shipToState ? stateName(shipToState) : 'this address'}.
        Get in touch and the Fi team will sort it out — there is nothing for you to upload.
      </StateBlock>
    );
  }

  if (rejected.length > 0) {
    const first = rejected[0];
    return (
      <>
        <StateBlock tone="bad" title="Document Not Accepted">
          {`The Fi team couldn't verify your ${documentName(first)}. Upload a replacement to keep ordering ${programName}s.`}
        </StateBlock>

        <div className={styles.stateFoot}>
          {rejected.map((entry) => {
            const doc = entry.document;
            if (!doc) {
              return null;
            }
            const uploading = uploadingKey === keyFor(filedType(entry), entry.state);

            return (
              <div className={styles.blockedDoc} key={keyFor(entry.documentType, entry.state)}>
                <FileCard
                  name={doc.originalFileName}
                  sub={doc.reviewedAt ? `Reviewed ${formatDate(doc.reviewedAt)}` : 'Not accepted'}
                >
                  <StatusPill tone="rejected">Not accepted</StatusPill>
                </FileCard>
                {doc.rejectionReason && (
                  <p className={styles.rejectNote}>
                    <b>Reason:</b> {doc.rejectionReason}
                  </p>
                )}

                <UploadBox
                  compact
                  title={`Upload a replacement ${entry.documentType === 'NONPROFIT_LETTER' ? 'letter' : documentName(entry)}`}
                  hint={
                    entry.documentType === 'NONPROFIT_LETTER'
                      ? "The letter the IRS sent confirming your organization's 501(c)⁠(3) status. PDF up to 20MB."
                      : withFileLimit(
                          stateDocumentHint(filedType(entry), entry.state) ?? 'Your state exemption certificate.',
                          filedType(entry),
                        )
                  }
                  onUpload={() => onPickFile(filedType(entry), entry.state)}
                  uploading={uploading}
                />
              </div>
            );
          })}

          {fileError && <div className={styles.error}>{fileError}</div>}
        </div>
      </>
    );
  }

  // The order before approval is spent and the letter was never filed, so there is nothing to wait on yet.
  if (letters.length > 0 && inReview.length === 0) {
    return (
      <>
        <StateBlock tone="wait" title="Documents Needed">
          {`Upload your ${documentName(letters[0])} to place your next ${programName} order.`}
        </StateBlock>

        <div className={styles.stateFoot}>
          {unfiled.map((entry) => (
            <UnfiledUpload
              key={keyFor(entry.documentType, entry.state)}
              entry={entry}
              uploading={uploadingKey === keyFor(entry.documentType, entry.state)}
              onPickFile={onPickFile}
            />
          ))}

          {fileError && <div className={styles.error}>{fileError}</div>}
        </div>
      </>
    );
  }

  // Two ways in, and they are not the same news. Blocked on arrival, the rescue is being refused and
  // wants to know which document and for how long. Having just replaced one, they want confirmation.
  const waiting = inReview.length > 0 ? documentName(inReview[0]) : 'documents';

  return (
    <>
      <StateBlock tone="wait" title="Documents Awaiting Review">
        {justReplaced
          ? `Thanks. Your next ${programName} order can be placed once the Fi team approves your replacement ${
              inReview[0] ? replacedNoun(inReview[0]) : 'documents'
            }.`
          : `Your next ${programName} order can be placed once the Fi team approves your ${waiting}. Reviews usually take a few business days.`}
      </StateBlock>

      <div className={styles.stateFoot}>
        {inReview.map((entry) => {
          const doc = entry.document;
          if (!doc) {
            return null;
          }

          return (
            <FileCard
              key={keyFor(entry.documentType, entry.state)}
              name={doc.originalFileName}
              sub={uploadedJustNow(doc) ? 'Uploaded just now' : `Uploaded ${formatDate(doc.uploadedAt)}`}
            >
              <StatusPill tone="pending">In review</StatusPill>
            </FileCard>
          );
        })}

        {unfiled.map((entry) => {
          const uploading = uploadingKey === keyFor(entry.documentType, entry.state);

          return (
            <div className={styles.alsoNeeded} key={keyFor(entry.documentType, entry.state)}>
              <p className={styles.alsoLead}>
                While you wait, you can file the {documentName(entry)} your next order will need.
              </p>

              <UnfiledUpload entry={entry} uploading={uploading} onPickFile={onPickFile} />
            </div>
          );
        })}

        {fileError && <div className={styles.error}>{fileError}</div>}
      </div>
    </>
  );
}
